import { Visibility } from "@/shared/enums/visibility";

interface CellOptions {
	data: {
		title: string;
		catalogRecord?: {
			summary: string;
			issued: string;
		};
		terms?: string[];
		accessRights: {
			code: string;
		};
		translatedAccessRights?: string;
		owner?: {
			name: string;
		};
		categories?: string[];
	};
}

interface ColumnConfig {
	caption: string;
	dataField: string;
	width?: string;
	minWidth?: number;
	visible?: boolean;
	dataType?: string;
	format?: string;
	allowHeaderFiltering?: boolean;
	allowSorting?: boolean;
	allowFiltering?: boolean;
	filterOperations?: string[];
	cellTemplate?: (container: HTMLElement, options: CellOptions) => void;
}

export const getDatasetColumns = (
	t: (key: string) => string,
): ColumnConfig[] => {
	const DEFAULT_FILTER_OPERATIONS = [
		"contains",
		"startswith",
		"endswith",
		"=",
		"<>",
		"isblank",
		"isnotblank",
	];

	return [
		{
			caption: t("catalogue.dataset-name"),
			dataField: "title",
			minWidth: 200,
			allowHeaderFiltering: false,
			allowSorting: true,
			allowFiltering: true,
			filterOperations: DEFAULT_FILTER_OPERATIONS,
		},
		{
			caption: t("catalogue.description"),
			dataField: "catalogRecord.summary",
			minWidth: 250,
			allowHeaderFiltering: false,
			allowSorting: true,
			allowFiltering: true,
			filterOperations: DEFAULT_FILTER_OPERATIONS,
		},
		{
			caption: t("catalogue.terms"),
			dataField: "terms",
			minWidth: 200,
			allowHeaderFiltering: false,
			allowSorting: false,
			allowFiltering: false,
		},
		{
			caption: t("catalogue.visibility"),
			dataField: "accessRights.code",
			minWidth: 150,
			visible: true,
			allowSorting: true,
			allowFiltering: false,
			cellTemplate: (container: HTMLElement, options: CellOptions): void => {
				renderVisibilityChip(container, options);
			},
		},
		{
			caption: t("catalogue.owner"),
			dataField: "owner.name",
			minWidth: 200,
			allowHeaderFiltering: false,
			allowSorting: true,
			allowFiltering: true,
			filterOperations: DEFAULT_FILTER_OPERATIONS,
		},
		{
			caption: t("catalogue.categories"),
			dataField: "categories",
			minWidth: 200,
			allowHeaderFiltering: false,
			allowSorting: false,
			allowFiltering: true,
			filterOperations: DEFAULT_FILTER_OPERATIONS,
		},
		{
			caption: t("catalogue.dateCreated"),
			dataField: "catalogRecord.issued",
			dataType: "datetime",
			format: "dd/MM/yyyy HH:mm",
			minWidth: 150,
			allowHeaderFiltering: false,
			allowSorting: true,
			allowFiltering: false,
		},
	];
};

export const renderVisibilityChip = (
	container: HTMLElement,
	options: CellOptions,
): void => {
	const translated =
		options.data.translatedAccessRights ?? options.data.accessRights?.code;
	const visibilityKey = options.data.accessRights?.code;

	if (
		visibilityKey === Visibility.PUBLIC ||
		visibilityKey === Visibility.PRIVATE ||
		visibilityKey === Visibility.RESTRICTED
	) {
		const wrapperElement = document.createElement("div");
		wrapperElement.classList.add("visibility-with-dot");

		const dotElement = document.createElement("i");
		dotElement.classList.add("mdi", "mdi-circle", "content-circle");

		const textElement = document.createElement("span");
		textElement.textContent = translated;

		if (visibilityKey === Visibility.PUBLIC) {
			dotElement.classList.add("content-visibility-circle-green");
		} else if (visibilityKey === Visibility.PRIVATE) {
			dotElement.classList.add("content-visibility-circle-red");
		} else if (visibilityKey === Visibility.RESTRICTED) {
			dotElement.classList.add("content-visibility-circle-orange");
		}

		wrapperElement.appendChild(dotElement);
		wrapperElement.appendChild(textElement);
		container.appendChild(wrapperElement);
	}
};
